import { VIDEO_QUALITIES, type Studio, type UpdateStudioBody, type VideoQuality } from '@greenroom/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api'
import { q } from '../lib/queries'
import { Button } from './ui/button'
import { Dialog } from './ui/dialog'
import { Field } from './ui/field'
import { Select } from './ui/select'

/** Keeps the single-studio query and the team list in step with what the server returned, without reloading the list. */
export function useStudioCache() {
  const qc = useQueryClient()
  return {
    put: (studio: Studio) => {
      qc.setQueryData(q.studio(studio.id).queryKey, studio)
      qc.setQueryData(q.studios(studio.teamId).queryKey, (list) => {
        if (!list) return list
        return list.some((s) => s.id === studio.id) ? list.map((s) => (s.id === studio.id ? studio : s)) : [studio, ...list]
      })
    },
    remove: (studio: Studio) => {
      qc.removeQueries({ queryKey: q.studio(studio.id).queryKey })
      qc.setQueryData(q.studios(studio.teamId).queryKey, (list) => list?.filter((s) => s.id !== studio.id))
    },
  }
}

export function CreateStudioDialog({ teamId, open, onOpenChange }: { teamId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const cache = useStudioCache()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: (studioName: string) => api<Studio>('/api/studios', { body: { teamId, name: studioName } }),
    onSuccess: (studio) => {
      cache.put(studio)
      onOpenChange(false)
      setName('')
      navigate(`/studios/${studio.id}`)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) create.mutate(name.trim())
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New studio" description="A studio is a permanent room with its own guest link. Use one per show.">
      <form onSubmit={submit} className="space-y-6">
        <Field label="Studio name" placeholder="The Weekly Show" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus required />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!name.trim()}>
            Create studio
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

const QUALITY_OPTIONS: { value: VideoQuality; label: string }[] = VIDEO_QUALITIES.map((v) => ({
  value: v,
  label: v === '1080p' ? '1080p Full HD (needs a good camera and upload)' : '720p HD',
}))

export function StudioSettingsDialog({ studio, open, onOpenChange }: { studio: Studio; open: boolean; onOpenChange: (o: boolean) => void }) {
  const cache = useStudioCache()
  const [form, setForm] = useState({ name: studio.name, videoQuality: studio.videoQuality, backupRecording: studio.backupRecording })
  useEffect(() => {
    if (open) setForm({ name: studio.name, videoQuality: studio.videoQuality, backupRecording: studio.backupRecording })
  }, [open, studio])
  const save = useMutation({
    mutationFn: (body: UpdateStudioBody) => api<Studio>(`/api/studios/${studio.id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => {
      cache.put(updated)
      onOpenChange(false)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate({ ...form, name: form.name.trim() })
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Studio settings">
      <form onSubmit={submit} className="space-y-5">
        <Field label="Studio name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} required />
        <div>
          <span className="field-label">Recording quality</span>
          <Select
            label="Recording quality"
            value={form.videoQuality}
            onChange={(videoQuality) => setForm({ ...form, videoQuality })}
            options={QUALITY_OPTIONS}
          />
          <p className="mt-1.5 text-[13px] text-faint">Each participant records locally at this resolution, independent of their connection.</p>
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:border-border-strong">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-accent"
            checked={form.backupRecording}
            onChange={(e) => setForm({ ...form, backupRecording: e.target.checked })}
          />
          <span>
            <span className="block text-sm font-medium">Cloud backup recording</span>
            <span className="mt-0.5 block text-[13px] text-muted">
              The server also records what it receives from everyone. Lower quality, but it covers a participant whose device fails.
            </span>
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!form.name.trim()}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
